import React from 'react';
import styled from 'styled-components';
import { SxProps, Theme } from '@mui/material';
import LoadingButton, {
  type Props as LoadingButtonProps,
} from '../Button/LoadingButton';
import { Flex } from '../Flex';
import { ComponentSize, COMPONENT_SIZE_MAP } from '../../types/types';
import {
  componentToken,
  fontSize,
  fontWeight,
  radius,
  space,
} from '../../style/utils';

const ButtonWrapper = styled(Flex)`
  position: relative;
  height: 100%;
  :hover {
    background: ${({ theme }) => theme.palette.surface.track};
  }
`;

const InputWrapper = styled(Flex)<{
  $disabled?: boolean;
  $size?: ComponentSize;
}>`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.border.controlStrong};
  border-radius: ${radius('lg')};
  height: ${({ $size }) => COMPONENT_SIZE_MAP[$size || 'medium']}px;
  overflow: hidden;
  ${({ $disabled, theme }) =>
    $disabled &&
    `
    background: ${theme.palette.surface.muted};
    cursor: not-allowed;
  `}
`;

export const TextInputLabel = styled.input`
  width: 100%;
  font-size: ${fontSize('base')} !important;
  padding: ${space(0, 'mdPlus')};
  border: none;
  background: none;
  color: ${({ theme }) => theme.palette.content.primary};
  font-weight: ${({ value, theme }) =>
    value ? theme.typography.fontWeightBold : fontWeight('regular')};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.palette.content.subtle};
    opacity: 1;
  }

  &:disabled {
    background: ${({ theme }) => theme.palette.surface.muted};
    color: ${({ theme }) => theme.palette.content.placeholder};
    cursor: not-allowed;
  }
`;

export const ActionLoadingButton = styled(LoadingButton)<{
  component?: string;
  disabled?: boolean;
}>`
  &.MuiLoadingButton-root {
    height: 100%;
    width: 90px;
    box-shadow: none;
    background: ${({ theme, disabled }) =>
      disabled
        ? theme.tokens.components.textInputWithButton.action.disabledBg
        : theme.tokens.components.textInputWithButton.action.bg};
    color: ${({ theme, disabled }) =>
      disabled
        ? theme.tokens.components.textInputWithButton.action.disabledFg
        : theme.tokens.components.textInputWithButton.action.fg};
    overflow: hidden;
    border: 1px solid
      ${({ theme, disabled }) =>
        disabled
          ? theme.tokens.components.textInputWithButton.action.disabledBorder
          : theme.tokens.components.textInputWithButton.action.border};
    border-radius: ${radius(0, 'lg', 'lg', 0)};
    :hover {
      background: ${componentToken('textInputWithButton.action.hoverBg')};
      color: ${({ theme, disabled }) =>
        disabled
          ? theme.tokens.components.textInputWithButton.action.disabledFg
          : theme.tokens.components.textInputWithButton.action.hoverFg};
      border-color: ${({ theme, disabled }) =>
        disabled
          ? theme.tokens.components.textInputWithButton.action.disabledBorder
          : theme.tokens.components.textInputWithButton.action.hoverBorder};
      cursor: pointer;
    }
  }
`;

export type TextInputWithButtonProps = {
  value: string;
  onChange: (value: string) => void;
  onButtonClick: () => void;
  placeholder?: string;
  buttonText?: string;
  disabled?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
  size?: ComponentSize;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  buttonProps?: Partial<
    Omit<
      LoadingButtonProps,
      'component' | 'variant' | 'disableElevation' | 'onClick' | 'loading'
    >
  >;
};

const TextInputWithButton = React.forwardRef(
  (
    {
      value = '',
      onChange,
      onButtonClick,
      placeholder,
      buttonText = 'Submit',
      disabled = false,
      loading = false,
      sx,
      size,
      inputProps,
      buttonProps,
    }: TextInputWithButtonProps,
    ref: any,
  ) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    const allButtonProps = {
      ...buttonProps,
      disableElevation: true,
      variant: 'outlined' as const,
      onClick: onButtonClick,
      disabled: disabled || loading,
      loading: loading,
    };

    return (
      <InputWrapper
        ref={ref}
        sx={{ width: '100%', ...sx }}
        $disabled={disabled}
        $size={size}
      >
        <TextInputLabel
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          {...inputProps}
        />
        <ButtonWrapper>
          <ActionLoadingButton {...allButtonProps}>
            {buttonText}
          </ActionLoadingButton>
        </ButtonWrapper>
      </InputWrapper>
    );
  },
);

TextInputWithButton.displayName = 'TextInputWithButton';

export default TextInputWithButton;
